import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Empty,
  Spin,
  Popconfirm,
  Modal,
  Input,
} from "antd";
import { PlusOutlined, DeleteOutlined, ApiOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL, getCredentials } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { SquareIntegration } from "./types";
import { SquareIntegrationForm } from "./Form";

const { Text } = Typography;

type SquareIntegrationEditorProps = {
  restaurantId: string;
};

export const SquareIntegrationEditor: React.FC<SquareIntegrationEditorProps> = ({
  restaurantId,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { deleteSuccess, notifyError, updateSuccess } = useNotification();
  const [integrations, setIntegrations] = useState<SquareIntegration[] | undefined>(undefined);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<SquareIntegration | null>(null);
  const [taxPercentageInput, setTaxPercentageInput] = useState<string>("");
  const [taxPercentageError, setTaxPercentageError] = useState<string | null>(null);
  const [savingTaxPercentage, setSavingTaxPercentage] = useState(false);

  const refreshIntegrations = () => {
    getFetchT<{ integrations: SquareIntegration[] }>(
      `${API_URL}/v1/r/${restaurantId}/square/integrations`,
      (result) => setIntegrations(result.integrations || []),
      (err) => {
        notifyError(t("failed_to_fetch_integrations"), err);
        setIntegrations([]);
      }
    );
  };

  useEffect(() => {
    refreshIntegrations();
  }, [restaurantId]);

  const handleDelete = (integrationId: string) => {
    setDeleting(integrationId);
    fetch(`${API_URL}/v1/r/${restaurantId}/square/integrations/${integrationId}`, {
      credentials: "include",
      method: "DELETE",
      headers: {
        Authorization: `Basic ${getCredentials()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        deleteSuccess(t("square_integration"));
        setIntegrations(undefined);
        refreshIntegrations();
      })
      .catch((err) => {
        err.text().then((errorMessage: string) => {
          notifyError(t("failed_to_delete_integration"), errorMessage);
        });
      })
      .finally(() => {
        setDeleting(null);
      });
  };

  const onChange = () => {
    setIntegrations(undefined);
    refreshIntegrations();
  };

  const validateTaxPercentage = (value: string): boolean => {
    if (value === "") {
      setTaxPercentageError(null);
      return true;
    }
    const regex = /^\d+(\.\d{1,6})?$/;
    if (!regex.test(value)) {
      setTaxPercentageError(t("tax_percentage_invalid_format_6_decimals"));
      return false;
    }
    const numValue = parseFloat(value);
    if (numValue < 0) {
      setTaxPercentageError(t("tax_percentage_must_be_positive"));
      return false;
    }
    setTaxPercentageError(null);
    return true;
  };

  const handleOpenEditModal = (integration: SquareIntegration) => {
    setEditingIntegration(integration);
    setTaxPercentageInput(integration.tax_percentage || "");
    setTaxPercentageError(null);
  };

  const handleCloseEditModal = () => {
    setEditingIntegration(null);
    setTaxPercentageInput("");
    setTaxPercentageError(null);
  };

  const handleTaxPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTaxPercentageInput(value);
    validateTaxPercentage(value);
  };

  const handleSaveTaxPercentage = () => {
    if (!editingIntegration) return;
    if (!validateTaxPercentage(taxPercentageInput)) return;

    setSavingTaxPercentage(true);
    const taxPercentage = taxPercentageInput === "" ? null : taxPercentageInput;

    fetch(
      `${API_URL}/v1/r/${restaurantId}/square/integrations/${editingIntegration.id}/tax_percentage`,
      {
        credentials: "include",
        method: "PUT",
        headers: {
          Authorization: `Basic ${getCredentials()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tax_percentage: taxPercentage }),
      }
    )
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        updateSuccess(t("tax_percentage"));
        handleCloseEditModal();
        setIntegrations(undefined);
        refreshIntegrations();
      })
      .catch((err) => {
        err.text().then((errorMessage: string) => {
          notifyError(t("failed_to_update_tax_percentage"), errorMessage);
        });
      })
      .finally(() => {
        setSavingTaxPercentage(false);
      });
  };

  if (integrations === undefined) {
    return <Spin size="large" />;
  }

  const columns = [
    {
      title: t("merchant_id"),
      dataIndex: "merchant_id",
      key: "merchant_id",
    },
    {
      title: t("location_id"),
      dataIndex: "location_id",
      key: "location_id",
    },
    {
      title: t("tax_percentage"),
      dataIndex: "tax_percentage",
      key: "tax_percentage",
      render: (value: string | undefined) => value || "-",
    },
    {
      title: t("use_for_online_payment"),
      dataIndex: "use_for_online_payment",
      key: "use_for_online_payment",
      render: (value: boolean) => (value ? t("yes") : t("no")),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, record: SquareIntegration) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenEditModal(record)}>
            {t("edit_tax_percentage")}
          </Button>
          <Popconfirm
            title={t("delete_integration_confirm")}
            description={t("delete_integration_description")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} loading={deleting === record.id}>
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("add_square_integration")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <SquareIntegrationForm restaurantId={restaurantId} onSuccess={onChange} />
      </Card>

      <Card
        title={
          <Space>
            <ApiOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_integrations")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {integrations.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_integrations_available")}</Text>}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={integrations}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t("total")} ${total} ${t("integrations")}`,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        )}
      </Card>

      <Modal
        title={t("edit_tax_percentage")}
        open={editingIntegration !== null}
        onCancel={handleCloseEditModal}
        onOk={handleSaveTaxPercentage}
        okText={t("save")}
        cancelText={t("cancel")}
        confirmLoading={savingTaxPercentage}
        okButtonProps={{ disabled: taxPercentageError !== null }}
      >
        <div style={{ marginBottom: token.marginSM }}>
          <Text>{t("enter_tax_percentage_6_decimals")}</Text>
        </div>
        <Input
          value={taxPercentageInput}
          onChange={handleTaxPercentageChange}
          placeholder={t("enter_tax_percentage")}
          status={taxPercentageError ? "error" : undefined}
        />
        {taxPercentageError && (
          <Text type="danger" style={{ display: "block", marginTop: token.marginXS }}>
            {taxPercentageError}
          </Text>
        )}
      </Modal>
    </div>
  );
};
